import React, { useState } from "react";
import { Button, Form, FormGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";

import { f0Methods, technologies, visibilityStatuses, booleans, optionForValue } from "services/EnumService";
import { TextInput, SelectInput } from "components/UI/Forms";

import "./AiModelFullForm.scss";

const AiModelFullForm = (props) => {
  const aiModel = props.aiModel;
  const [artist, setArtist] = useState(aiModel.artist || "");
  const [description, setDescription] = useState(aiModel.description || "");
  const [indexFile, setIndexFile] = useState(aiModel.model_index_file_location || "");
  const [modelName, setModelName] = useState(aiModel.model_name || "");
  const [epochs, setEpochs] = useState(aiModel.epochs ?? null);
  const [technology, setTechnology] = useState(aiModel.technology || "v2");
  const [f0, setF0] = useState(aiModel.f0 || "");
  const [visibilityStatus, setVisibilityStatus] = useState(aiModel.visibility_status || "");
  const [pitchGuidance, setPitchGuidance] = useState(aiModel.pitch_guidance || true);
  const [sampleRateK, setSampleRateK] = useState(aiModel.sample_rate_k || 48);
  const [useNoiseFilter, setUseNoiseFilter] = useState(aiModel.use_noise_filter || false);
  const [crepeHopLength, setCrepeHopLength] = useState(aiModel.crepe_hop_length ?? null);
  const [showDescription, setShowDescription] = useState(false);

  const handleSubmit = (event) => {
    event.preventDefault();

    let aiModel = {
      artist: artist,
      description: description,
      indexFile: indexFile,
      modelName: modelName,
      epochs: epochs,
      technology: technology.value,
      f0: f0.value,
      visibilityStatus: visibilityStatus.value,
      pitchGuidance: pitchGuidance.value,
      sampleRateK: sampleRateK,
      useNoiseFilter: useNoiseFilter,
      crepeHopLength: crepeHopLength,
    };

    props.handleSubmit(aiModel);
  };

  const validateForm = () => {
    return artist && modelName && indexFile && visibilityStatus;
  };

  return (
    <>
      <div className="ai-model-form-component text-center">
        <Form onSubmit={handleSubmit}>
          <div className="instructions-text text-start">
            Select Model Parameters
            <span className="icon-button ms-3" onClick={() => setShowDescription(!showDescription)}>
              <FontAwesomeIcon icon={faQuestionCircle} />
            </span>
          </div>
          {aiModel && (
            <div>
              <div className="row">
                <div className="col-12 col-lg-6 input-table">
                  <TextInput
                    id="artist"
                    label="Artist"
                    value={artist}
                    onChange={(event) => setArtist(event.target.value)}
                    description="Artist name"
                    showDescription={showDescription}
                  />
                  <TextInput
                    id="description"
                    label="Description"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    description="Description of model"
                    showDescription={showDescription}
                    optional={true}
                  />
                  <TextInput
                    id="indexFile"
                    label="Index File"
                    value={indexFile}
                    onChange={(event) => setIndexFile(event.target.value)}
                    description="Index file path"
                    showDescription={showDescription}
                  />
                  <TextInput
                    id="modelName"
                    label="Model Name"
                    value={modelName}
                    onChange={(event) => setModelName(event.target.value)}
                    description="Model name"
                    showDescription={showDescription}
                  />
                  <TextInput
                    id="sampleRateK"
                    label="Sample Rate (kHz)"
                    value={sampleRateK}
                    onChange={(event) => setSampleRateK(event.target.value)}
                    description="Sample rate in kHz"
                    showDescription={showDescription}
                    optional={true}
                  />
                  <TextInput
                    id="epochs"
                    label="Epochs"
                    value={epochs}
                    onChange={(event) => setEpochs(event.target.value)}
                    description="Number of epochs"
                    showDescription={showDescription}
                    optional={true}
                  />
                </div>
                <div className="col-12 col-lg-6 input-table">
                  <SelectInput
                    id="visibilityStatus"
                    label="Visibility Status"
                    value={optionForValue(visibilityStatus, visibilityStatuses)}
                    onChange={(event) => setVisibilityStatus(event)}
                    options={visibilityStatuses}
                    description="Visibility status"
                    showDescription={showDescription}
                  />
                  <SelectInput
                    id="technology"
                    label="Technology"
                    value={optionForValue(technology, technologies)}
                    onChange={(event) => setTechnology(event)}
                    options={technologies}
                    description="Technology used"
                    showDescription={showDescription}
                    optional={true}
                  />
                  <SelectInput
                    id="f0"
                    label="AI Pitch Algorithm"
                    value={optionForValue(f0, f0Methods)}
                    onChange={(event) => setF0(event)}
                    options={f0Methods}
                    description="Pitch detection algorithm."
                    showDescription={showDescription}
                    disabled={false}
                    optional={true}
                  />
                  <SelectInput
                    id="pitchGuidance"
                    label="Pitch Guidance"
                    value={optionForValue(pitchGuidance, booleans)}
                    onChange={(event) => setPitchGuidance(event)}
                    options={booleans}
                    description="Pitch guidance"
                    showDescription={showDescription}
                    optional={true}
                  />
                  <SelectInput
                    id="useNoiseFilter"
                    label="Use Noise Filter"
                    value={optionForValue(useNoiseFilter, booleans)}
                    onChange={(event) => setUseNoiseFilter(event)}
                    options={booleans}
                    description="Use noise filter"
                    showDescription={showDescription}
                    optional={true}
                  />
                  <TextInput
                    id="crepeHopLength"
                    label="Crepe Hop Length"
                    value={crepeHopLength}
                    onChange={(event) => setCrepeHopLength(event.target.value)}
                    description="Crepe hop length"
                    showDescription={showDescription}
                    optional={true}
                  />

                </div>
              </div>
              <div>
                  <div className="required-field-text">
                    <span className="asterisk">*</span> Required Field
                  </div>
                <FormGroup controlId="buttons">
                  <Button disabled={!validateForm() || props.loading} type="submit">
                    Submit
                  </Button>
                  {props.cancelForm && (
                    <Button className="btn-secondary" onClick={props.cancelForm}>
                      Cancel
                    </Button>
                  )}
                </FormGroup>
              </div>
            </div>
          )}
        </Form>
      </div>
    </>
  );
};

export default AiModelFullForm;
